import { authGuard } from '@auth0/auth0-vue';

import { isEmbedded } from '@/play-editor/frame';

import { isAuthDisabled } from '@/store/auth/auth-utils';
import { RouteName } from '@/router/route-names';
import { logger } from '@/shared/logging';
import {
    auth,
    authCallbackError,
    clearAuth0LocalStorage,
    getAccessToken,
} from '@/plugins/auth';

export function sendAuthRedirect(router, location) {
    if (!router.currentRoute.value.meta?.isPublic) {
        if (isEmbedded()) {
            return router.replace({ name: RouteName.loggedOutEmbed });
        }

        return router.push(location);
    }

    return null;
}

const log = logger('router.intercept');

/**
 * Builds the /auth-error route with Auth0's error details as query params.
 * URLSearchParams handles encoding so error descriptions with spaces or
 * special characters round-trip safely to the page component.
 */
export function getAuthErrorRoute(authError) {
    const { error, error_description: errorDescription } = authError || {};
    const query = new URLSearchParams();

    if (error) query.set('error', error);
    if (errorDescription) query.set('error_description', errorDescription);

    const queryString = query.toString();

    return `/auth-error${queryString ? `?${queryString}` : ''}`;
}

/**
 * Hydrate the Vuex auth store from the Auth0 SPA SDK so the rest of the app
 * (which reads `state.auth.session.jwt`, `auth/isLoggedIn`, etc.) keeps
 * working without changes. Triggered after `authGuard(to)` confirms the user
 * is authenticated.
 */
async function syncAuth0SessionIntoStore(store) {
    if (!auth) return;

    try {
        const accessToken = await getAccessToken();

        if (!accessToken) return;

        // Skip the no-op dispatch when the store already holds this token for
        // a logged-in session — avoids a Vuex mutation + persisted-state write
        // on every navigation. Optional chaining: the router spec store mock
        // is only `{ auth: {} }`.
        const currentJwt = store.state?.auth?.session?.jwt;
        const isLoggedIn = store.getters?.['auth/isLoggedIn'];
        const hasAccounts = Boolean(store.state?.auth?.accounts);

        if (currentJwt && currentJwt === accessToken && isLoggedIn) {
            // Rehydrated JWTs arrive without accounts/roles (not persisted).
            // Fetch them without a redundant LOGIN dispatch.
            if (!hasAccounts) {
                await store.dispatch('auth/FETCH_USER_DETAILS');
            }

            return;
        }

        const auth0User = auth.user?.value ?? {};

        await store.dispatch('auth/LOGIN_WITH_AUTH0', { accessToken, auth0User });
    } catch (e) {
        log.warn('Failed to hydrate auth store from Auth0 SDK', e);
    }
}

export const beforeResolveRouteAuth = async (to, _, next, store, i18n, router) => {
    try {
        const inboundTicket = to.query.ticket || to.query.userToken;
        const embedded = isEmbedded();

        // Auth0 branch for the standalone (non-embedded) flow. Embedded
        // iframe usage stays on the ticket-in-URL path below, so the keap-web
        // parent contract is untouched.
        if (!embedded && !isAuthDisabled() && auth) {
            if (to.meta?.isPublic) {
                return next();
            }

            // Terminal Auth0 callback error only — do NOT key off the shared
            // auth.error ref (set by any failed SDK call, including silent
            // token renewal).
            if (authCallbackError) {
                store.commit('auth/SESSION_UNAUTHORIZED');
                clearAuth0LocalStorage();

                return next(getAuthErrorRoute(authCallbackError));
            }

            // Persist the deep link so `onRedirectCallback` in plugins/auth.ts
            // can restore it after the Universal Login round trip even if
            // appState gets dropped (OAuth error, refresh failure, etc.).
            sessionStorage.setItem('auth_redirect_target', to.fullPath);

            const guardResult = await authGuard(to);

            if (guardResult === false) {
                return next(false);
            }

            await syncAuth0SessionIntoStore(store);

            return next();
        }

        if (inboundTicket) {
            // Remove ticket and redirect parameters from request uri.
            const {
                query: {
                    ticket, userToken, redirect, ...query
                }, ...nextRoute
            } = to;
            let jwt;

            if (Array.isArray(inboundTicket)) { // in case of multiple, take the last one
                jwt = inboundTicket[inboundTicket.length - 1];
            } else {
                jwt = inboundTicket;
            }

            await store.dispatch('auth/LOGIN', jwt);
            nextRoute.query = query;
            nextRoute.replace = true;

            if (nextRoute.name === 'logout') {
                nextRoute.name = 'home';
            }

            // Re-fetch feature flags now that we have a JWT
            // This handles cases where feature flags were fetched without auth during store init
            store.dispatch('flags/FETCH_FEATURE_FLAGS', true).catch((error) => {
                log.error('Failed to re-fetch feature flags after JWT obtained', error);
            });

            if (store.state.auth.failed && !embedded) {
                // just let the oops page be shown
                return next();
            }

            return next(nextRoute);
        }

        await store.dispatch('auth/VERIFY_LOGIN');

        const isLoggedIn = store.getters['auth/isLoggedIn'];

        const authDisabled = isAuthDisabled();

        if (to.meta.isPublic || authDisabled) {
            return next();
        }

        if (!isLoggedIn && embedded) {
            return next({ name: RouteName.loggedOutEmbed });
        }

        return next();
    } catch (e) {
        log.severe('Error in auth handler', e);

        return next({ name: 'error' });
    }
};
